"use client";

import { useTranslation } from "react-i18next";
import ImageWithSkeleton from "@/common/ImageWithSkeleton";
import { stripHtml } from "@/utils/stripHtml";
import Link from "next/link";
import ScrollReveal from "@/common/ScrollReveal";
import useNewsSection from "./hooks/useNewsSection";
import NewsSectionLoading from "./components/NewsSectionLoading";

const NewsSection = () => {
  const { t } = useTranslation();
  const { activeSlide, posts, isLoading, goTo } = useNewsSection();

  if (isLoading) return <NewsSectionLoading />;

  if (posts.length === 0) return null;

  return (
    <section className="w-full py-12 sm:py-16 md:py-20 lg:py-32 xl:py-40">
      {/* 섹션 제목 */}
      <ScrollReveal className="max-w-[1920px] mx-auto mb-8 lg:mb-16">
        <h2 className="font-bold text-[clamp(1.5rem,2.5vw,2.5rem)] leading-tight text-gray-900 whitespace-pre-line px-[5%]">
          {t("NEWSROOM_DESCRIPTION")}
        </h2>
      </ScrollReveal>

      {/* 슬라이더: 썸네일 이미지(좌) + 게시글 목록(우) */}
      <div className="w-full flex flex-col md:flex-row">

        {/* 썸네일 이미지 — activeSlide에 따라 페이드 전환 */}
        <div className="w-full md:w-[60%] max-w-[1200px] shrink-0 overflow-hidden">
          <div className="relative w-full aspect-video" aria-live="polite">
            {posts.map((item, index) => (
              <div
                key={item.id}
                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                  activeSlide === index ? "opacity-100" : "opacity-0"
                }`}
                aria-hidden={activeSlide !== index}
              >
                {item.thumbnailUrl ? (
                  <ImageWithSkeleton
                    src={item.thumbnailUrl}
                    alt={item.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 60vw"
                    loading={index === 0 ? "eager" : "lazy"}
                    className="object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gray-200" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 게시글 목록 — 호버/포커스 시 해당 슬라이드로 이동 */}
        <div className="flex flex-col flex-1 min-w-0 justify-between px-[2.5%] mt-6 md:mt-0">
          <ul className="flex flex-col flex-1">
            {posts.map((item, index) => {
              const isActive = activeSlide === index;
              return (
                <li
                  key={item.id}
                  className={`relative flex-1 flex flex-col justify-center px-4 lg:px-8 py-4 sm:py-3 transition-colors border-b border-gray-100 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 lg:before:w-1.5 before:h-full before:rounded-full before:transition-colors ${
                    isActive
                      ? "before:bg-blue-500 bg-gray-50"
                      : "before:bg-transparent hover:bg-gray-50/50"
                  }`}
                  onMouseEnter={() => goTo(index)}
                  onFocus={() => goTo(index)}
                >
                  <Link href={`/newsroom/${item.id}`} className="block">
                    <span
                      className={`text-[clamp(1rem,2.5vw,1.25rem)] leading-snug block truncate ${isActive ? "font-extrabold text-blue-500" : "font-semibold text-gray-700"}`}
                    >
                      {item.title}
                    </span>
                    <span className="text-[clamp(0.875rem,2.5vw,1rem)] text-gray-400 leading-relaxed mt-1 line-clamp-2">
                      {stripHtml(item.content)}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>

          {/* 뉴스룸 전체보기 링크 */}
          <div className="mt-4 lg:mt-6 mb-4 flex items-center justify-end px-4 sm:px-5">
            <Link
              href="/newsroom"
              className="text-sm text-blue-500 underline underline-offset-6 inline-flex items-center opacity-80 hover:opacity-100 transition-opacity"
            >
              {t("NEWSROOM_VIEW_MORE")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
